import {
	type IAssistantHandoff,
	type ITranscriptMessage,
	OPEN_HANDOFF_STATUSES,
} from "types/assistant-handoff.types";
import { stripPageContext } from "utils/chatPageContext";
import type { ChatMessage } from "./useUserChat";

/**
 * Pure state transitions for the live staff handoff. Kept out of the hooks so
 * the rules (dedupe, placeholder removal, which banner shows) are testable
 * without mounting anything.
 */

/** How far back a socket echo of the customer's own message is matched. */
const ECHO_LOOKBACK = 4;

export function isHandoffOpen(
	handoff: IAssistantHandoff | null | undefined,
): boolean {
	return Boolean(handoff && OPEN_HANDOFF_STATUSES.includes(handoff.status));
}

export function transcriptToChatMessage(
	message: ITranscriptMessage,
): ChatMessage {
	return {
		id: message.id,
		role: message.author,
		content: message.content,
		timestamp: message.timestamp,
		...(message.staffName ? { staffName: message.staffName } : {}),
		...(message.kind ? { kind: message.kind } : {}),
	};
}

/**
 * Ids this client mints before the server has stored a turn (`user-<ms>`,
 * `assistant-<ms>`). Only these are ever matched by content; a message with a
 * server id is matched by id alone, so two real "yes" turns stay two turns.
 */
function isLocalId(id: string): boolean {
	return /^(user|assistant)-\d+$/.test(id);
}

/** How far apart an optimistic turn and its stored copy may be stamped. */
const SAME_TURN_WINDOW_MS = 2 * 60 * 1000;

/** Rows the transcript normalizer had to invent an id for (no server id). */
function isClientAssignedId(id: string): boolean {
	return id.startsWith("noid-");
}

function isCloseInTime(a: string, b: string): boolean {
	const left = Date.parse(a);
	const right = Date.parse(b);
	// An unknown or epoch-fallback stamp cannot rule a match out.
	if (!left || !right) return true;
	return Math.abs(left - right) <= SAME_TURN_WINDOW_MS;
}

/**
 * The same turn stored server-side: same author, same text once the
 * `[Current Page: ...]` prefix ChatBar adds to what it sends is removed
 * (comparing raw strings showed the customer's message twice), stamped
 * close together.
 */
function sameTurn(local: ChatMessage, server: ITranscriptMessage): boolean {
	return (
		local.role === server.author &&
		stripPageContext(local.content).trim() ===
			stripPageContext(server.content).trim() &&
		isCloseInTime(local.timestamp, server.timestamp)
	);
}

/**
 * The fetched history merged with whatever is already on screen. The fetch
 * races live socket pushes and the user's own sends (after a reload, a
 * session switch, or a reconnect), so neither side may be thrown away:
 * history first, then local messages the history does not already hold.
 * A local optimistic turn is dropped only when the server has the same turn.
 */
export function mergeLoadedTranscript(
	current: ChatMessage[],
	loaded: ITranscriptMessage[],
): ChatMessage[] {
	const loadedIds = new Set(loaded.map((message) => message.id));
	const unclaimed = loaded.filter((message) => !isLocalId(message.id));
	const remaining = current.filter((message) => {
		if (loadedIds.has(message.id)) return false;
		// An id-less row from an earlier load: this load holds its fresh copy.
		if (isClientAssignedId(message.id) && loaded.length > 0) return false;
		if (!isLocalId(message.id)) return true;
		const match = unclaimed.findIndex((server) => sameTurn(message, server));
		if (match < 0) return true;
		unclaimed.splice(match, 1);
		return false;
	});
	return [...loaded.map(transcriptToChatMessage), ...remaining];
}

/**
 * A pushed message (staff reply, system notice). Dedupe prefers the server id.
 * The one content match left is the echo of the customer's own turn, which is
 * on screen under a locally minted id: that message takes the server id
 * instead of appearing twice, so later merges dedupe it by id.
 */
export function appendIncomingMessage(
	current: ChatMessage[],
	incoming: ITranscriptMessage,
): ChatMessage[] {
	if (current.some((message) => message.id === incoming.id)) return current;

	if (incoming.author === "user") {
		// Only the most recent optimistic customer turn can be this echo.
		const from = Math.max(0, current.length - ECHO_LOOKBACK);
		let echoAt = -1;
		for (let index = current.length - 1; index >= from; index--) {
			const message = current[index];
			if (message.role === "user" && isLocalId(message.id)) {
				echoAt = sameTurn(message, incoming) ? index : -1;
				break;
			}
		}
		if (echoAt >= 0) {
			return current.map((message, index) =>
				index === echoAt ? { ...message, id: incoming.id } : message,
			);
		}
	}

	return [...current, transcriptToChatMessage(incoming)];
}

/**
 * When a person has the chat, the stream answers with `human_agent` and no
 * text. The empty assistant bubble made for the AI's reply would otherwise sit
 * in the transcript as a permanent "Thinking...".
 */
export function dropEmptyPlaceholder(
	current: ChatMessage[],
	placeholderId: string,
): ChatMessage[] {
	return current.filter(
		(message) =>
			message.id !== placeholderId ||
			message.content.trim() !== "" ||
			(message.blocks?.length ?? 0) > 0,
	);
}

/**
 * A `chat:handoff` event for this session replaces the local copy. A stale
 * event (older `updatedAt` than what we hold for the same handoff) is ignored
 * so an out-of-order socket delivery cannot resurrect a closed state.
 */
export function applyHandoffEvent(
	current: IAssistantHandoff | null,
	incoming: IAssistantHandoff | null,
	sessionId: string | undefined,
): IAssistantHandoff | null {
	if (!incoming || !sessionId || incoming.sessionId !== sessionId) {
		return current;
	}
	return isStaleHandoff(current, incoming) ? current : incoming;
}

/**
 * True when `incoming` is an older copy of the handoff we already hold
 * (`updatedAt` earlier than ours). Socket delivery is not ordered, and an
 * out-of-order event must not resurrect a state the chat already left.
 */
export function isStaleHandoff(
	current: IAssistantHandoff | null | undefined,
	incoming: IAssistantHandoff,
): boolean {
	if (!current || current.id !== incoming.id) return false;
	if (!current.updatedAt || !incoming.updatedAt) return false;
	return Date.parse(incoming.updatedAt) < Date.parse(current.updatedAt);
}

export type HandoffBanner =
	| { kind: "waiting"; canCancel: true }
	| { kind: "active"; staffName: string | null }
	| { kind: "ticket"; ticketId: number | null; ticketRef: string | null };

/**
 * Which banner the customer sees. Returned-to-AI, resolved and cancelled show
 * nothing: the system notice in the transcript already says what happened.
 */
export function handoffBanner(
	handoff: IAssistantHandoff | null,
	lastStaffName?: string | null,
): HandoffBanner | null {
	if (!handoff) return null;
	switch (handoff.status) {
		case "AWAITING_HUMAN":
			return { kind: "waiting", canCancel: true };
		case "HUMAN_ACTIVE":
			return {
				kind: "active",
				staffName: firstName(
					firstName(handoff.assignedStaffName)
						? handoff.assignedStaffName
						: lastStaffName,
				),
			};
		case "CONVERTED_TO_TICKET":
			return {
				kind: "ticket",
				ticketId: handoff.supportTicketId,
				ticketRef:
					handoff.supportTicketNumber ??
					(handoff.supportTicketId !== null
						? `#${handoff.supportTicketId}`
						: null),
			};
		default:
			return null;
	}
}

/**
 * A person's first name, or null when there is none to show. The API sends
 * "An Equility team member" when the staff name is unknown; taking its first
 * word would greet the customer with "An from Equility".
 */
function firstName(name: string | null | undefined): string | null {
	const trimmed = name?.trim();
	if (!trimmed || /equility/i.test(trimmed)) return null;
	return trimmed.split(/\s+/)[0];
}

/** The newest staff byline in the transcript, for the banner's fallback name. */
export function latestStaffName(messages: ChatMessage[]): string | null {
	for (let i = messages.length - 1; i >= 0; i--) {
		const message = messages[i];
		if (message.role === "staff" && message.staffName) {
			return message.staffName;
		}
	}
	return null;
}
